import {
  checkWalk,
  earliestFundingDay,
  hoursToFund,
  referenceCheck,
  walkFortnights,
} from '@actual-app/core/shared/mtxbudget-parcours';
import type {
  Check,
  LpSolve,
  WalkInputs,
  WalkStep,
} from '@actual-app/core/shared/mtxbudget-parcours';
import highsLoader from 'highs';
// `highs/runtime` is the package's WebAssembly file.
import highsWasmUrl from 'highs/runtime?url';

type Wanted = Parameters<typeof hoursToFund>[1];

/** What the solver is asked, as plain data: it crosses into a Worker. */
export type SolverTask =
  | { kind: 'walk'; inputs: WalkInputs }
  | { kind: 'walkChecked'; inputs: WalkInputs }
  | {
      kind: 'hoursToFund';
      inputs: WalkInputs;
      wanted: Wanted;
      date: string;
      maxHours: number;
    }
  | {
      kind: 'earliestFundingDay';
      inputs: WalkInputs;
      wanted: Wanted;
      maxHours: number;
      from: string;
    };

export type SolverResult<T extends SolverTask> = T extends { kind: 'walk' }
  ? WalkStep[]
  : T extends { kind: 'walkChecked' }
    ? { steps: WalkStep[]; checks: Check[] }
    : T extends { kind: 'hoursToFund' }
      ? { hours: number | null; missing: number }
      : string | null;

/** The linear-programming solver, HiGHS compiled to WebAssembly. */
export async function createSolve(): Promise<LpSolve> {
  const highs = await highsLoader({ locateFile: () => highsWasmUrl });
  return lp => {
    const result = highs.solve(lp);
    return {
      status: result.Status,
      objective: result.ObjectiveValue,
      values: Object.fromEntries(
        Object.entries(result.Columns).map(([name, column]) => [
          name,
          (column as { Primal: number }).Primal,
        ]),
      ),
    };
  };
}

/** Runs one task: in the Worker, or on the page when no Worker can start. */
export function runTask(task: SolverTask, solve: LpSolve): unknown {
  switch (task.kind) {
    case 'walk':
      return walkFortnights(task.inputs, solve);
    case 'walkChecked': {
      const steps = walkFortnights(task.inputs, solve);
      return {
        steps,
        checks: [...checkWalk(steps, task.inputs), referenceCheck(solve)],
      };
    }
    case 'hoursToFund':
      return hoursToFund(
        task.inputs,
        task.wanted,
        task.date,
        task.maxHours,
        solve,
      );
    default:
      return earliestFundingDay(
        task.inputs,
        task.wanted,
        task.maxHours,
        solve,
        task.from,
      );
  }
}
